import AbstractProvidedClass from "../../shared/AbstractProvidedClass";
import SpeciesLabel from "./SpeciesLabel";

export default class LabelCollection extends AbstractProvidedClass {
    parent = null;
    cutOff = 50;

    constructor(provider, data = {}) {
        super(provider);
        this.labels = data.labels ?? [];
        this.absX = data.labelX ?? 0;
        this.absY = data.labelY ?? 0;

        this.labels.map(l => l.setParent(this));
    }

    setParent(parent) {
        this.parent = parent;
        this.updatePosition();
    }

    display(g, currentSize, rotation = 0) {
        if (!this.settings.showLabels || this.labels.length == 0) {
            return;
        }
        const isHovered = this.parent?.isHovered;
        if (currentSize < this.cutOff && !isHovered) {
            return;
        }
        const effectiveSize = isHovered && currentSize < this.cutOff ? this.cutOff : currentSize;
        g.push();
        g.translate(this.parent.center.currentX, this.parent.center.currentY);
        g.fill(0, this.p.map(effectiveSize, 500, 1500, 255, 100, true));
        g.noStroke();
        g.textAlign(g.CENTER, g.CENTER);
        g.textSize(this.p.map(effectiveSize, 20, 1000, 10, 24));
        g.rectMode(g.CENTER);
        g.text(
            this.getLabels(effectiveSize).join('\n'),
            this.distance * this.view.scale * Math.cos(this.angle + rotation),
            this.distance * this.view.scale * Math.sin(this.angle + rotation)
        );
        g.pop();
    }

    hovered(viewX, viewY) {
        if (!this.settings.showLabels || this.settings.lockLabels || this.labels.length == 0 || this.parent.currentMaxSize < this.cutOff) {
            return false;
        }
        this.p.noStroke();
        this.p.textSize(this.p.map(this.parent.currentMaxSize, 20, 1000, 10, 24));
        let h = (this.p.textAscent() + this.p.textDescent()) * 0.9;
        let labels = this.getLabels(this.parent.currentMaxSize);
        let labelCount = labels.length;
        return !!labels.find((l, i) => {
            let w = this.p.textWidth(l);
            let baseline;
            if (viewX || viewY) {
                baseline = this.currentY + h * i;
            } else {
                baseline = this.parent.center.currentY + this.currentY + h * i;
            }
            if (labelCount == 2) {
                baseline -= h / 2;
            } else if (labelCount == 3) {
                baseline -= h;
            }
            if (viewX || viewY) {
                return viewX >= this.currentX - w / 2
                    && viewX <= this.currentX + w / 2
                    && viewY >= baseline - h / 2
                    && viewY <= baseline + h / 2;
            }
            return this.view.currentX >= this.parent.center.currentX + this.currentX - w / 2
                && this.view.currentX <= this.parent.center.currentX + this.currentX + w / 2
                && this.view.currentY >= baseline - h / 2
                && this.view.currentY <= baseline + h / 2;
        });
    }

    mouseOver(viewX, viewY) {
        if (this.hovered(viewX, viewY)) {
            this.cursor.move();
            return true;
        }
        return false;
    }

    drag(dx, dy) {
        this.absX += dx;
        this.absY += dy;
        this.updatePosition();
    }

    updatePosition() {
        this.distance = this.p.dist(
            0,
            0,
            this.absX,
            this.absY
        );
        this.angle = this.p.atan2(
            this.absY,
            this.absX
        );
    }

    clone() {
        return new LabelCollection(
            this.provider,
            {
                labels: this.labels.map(l => l.clone()),
                labelX: this.absX,
                labelY: this.absY,
            }
        );
    }

    export() {
        return {
            labels: this.labels.map(l => l.export()),
            labelX: this.absX,
            labelY: this.absY,
        }
    }

    getLabels(currentSize) {
        return this.labels.map(l => l.getValues())
            .flat()
            .filter(l => l)
            .slice(0, this.p.floor(
                this.p.map(currentSize, this.cutOff, 100, 1, 5)
            ));
    }

    getAllLabels() {
        return this.labels.map(l => l.getValues())
            .flat()
            .filter(l => l);
    }

    hasLabels() {
        return this.labels.filter(l => l.hasLabels()).length > 0;
    }

    addSpecies(species) {
        let keys = species.type == 'Species'
            ? (this.hasLabels() ? ['name'] : ['name', 'scientific_name'])
            : (this.hasLabels() ? ['parent_name'] : ['parent_name', 'name', 'scientific_name']);
        this.add(new SpeciesLabel(
            this.provider,
            { id: species.id, keys: keys }
        ));
    }

    removeSpecies(species) {
        this.labels = this.labels.filter(label => {
            return label.type == 'custom' || label.id != species.id;
        });
    }

    remove(label) {
        this.labels = this.labels.filter(l => l != label);
    }

    getValue(label, index = 0) {
        if (label.type == 'custom') {
            return label.value;
        }
        if (label.keys.length < index + 1) {
            return null;
        }
        return this.speciesCollection.get(label.id)[label.keys[index]];
    }

    getSpeciesLabel(speciesId) {
        return this.labels.find(label => label.id == speciesId);
    }

    getIndexOf(label) {
        return this.labels.indexOf(label);
    }

    add(label, index = this.labels.length) {
        this.labels.splice(index, 0, label);
        label.setParent(this);
    }

    move(oldIndex, newIndex) {
        let label = this.labels.splice(oldIndex, 1)[0];
        this.labels.splice(newIndex, 0, label);
    }

    replace(newLabel, newIndex) {
        let oldLabel = this.getSpeciesLabel(newLabel.id);
        let oldIndex = this.getIndexOf(oldLabel);
        this.labels.splice(oldIndex, 1);
        this.labels.splice(newIndex, 0, newLabel);
    }

    get currentX() {
        return this.absX * this.view.scale;
    }

    get currentY() {
        return this.absY * this.view.scale;
    }

    get length() {
        return this.labels.length;
    }

    get first() {
        return this.labels.reduce((acc, label) => {
            return acc || label.getValue();
        }, null);
    }

    get second() {
        if (this.labels[0] instanceof SpeciesLabel
            && this.labels[0].keys.length > 1
        ) {
            return this.labels[0].getValue(1);
        }
        for (let i = 1; i < this.labels.length; i++) {
            let label = this.labels[i];
            let value = label.getValue();
            if (value) {
                return value;
            }
        }
    }

    get third() {
        return this.labels.at(2) ?? '';
    }
}
